// @flow
import { alerts, feedback } from '~/core/constants';
import { BOOST_TARGET, MANUAL_TARGET_VALUE } from '~/core/constants/app-settings';
import { CACHE_PRODUCT_TEMPERATURE, CHANGE_PRODUCT_STATE, SET_HUMIDIFIER_TYPE } from './constants';
import type { CacheThermostatValueAction, ChangeProductStateAction, SetHumidifierTypeAction } from './types';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getOnlineNaThermostats, getThermostatControlMode } from './selectors';

import { buildKey } from '~/core/utils/app-settings';
import { changedProductState } from './actions';
import { deviceModes } from '~/core/constants/devices';
import { flowRight, noop } from 'lodash/fp';
import { getModeForProduct } from '~/core/state/products/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { patchProduct } from '~/core/api/products/products.api';
import { requestAlert } from '~/core/state/alerts/actions';
import { setAppSettings } from '~/core/state/app-settings/actions';
import { undoProductChange } from '~/core/state/products/actions';

const putAppSettings = flowRight(put, setAppSettings);

function* cacheThermostatValue({ payload }: CacheThermostatValueAction): Generator<Effect, *, *> {
    const { productId, controlMode, changeTypeKey, value } = payload;

    yield putAppSettings({ [buildKey(MANUAL_TARGET_VALUE, productId, controlMode, changeTypeKey)]: value });
}

function* watchCacheThermostatValue(): Generator<Effect, *, *> {
    yield takeEvery(CACHE_PRODUCT_TEMPERATURE, cacheThermostatValue);
}

// eslint-disable-next-line no-unused-vars
export function* changeProductStateSaga({ type, payload }: ChangeProductStateAction): Generator<Effect | noop, *, *> {
    const { productId, productType, ...patch } = payload;
    let nextPatch = patch;

    const mode = yield select(getModeForProduct, { id: productId });
    const controlMode = yield select(getThermostatControlMode, {
        id: productId
    });

    if (mode === deviceModes.MANUAL) {
        const isTemperatureChange = nextPatch.heat || nextPatch.cool;
        const changeTypeyKey = nextPatch.heat ? 'heat' : 'cool';

        if (isTemperatureChange) {
            yield putAppSettings({
                [buildKey(MANUAL_TARGET_VALUE, productId, controlMode, changeTypeyKey)]:
                    nextPatch.heat || nextPatch.cool
            });
        }

        if (!isTemperatureChange && patch.controlMode) {
            nextPatch = { controlMode: patch.controlMode };
        }
    }

    if (mode === deviceModes.SCHEDULE) {
        const isChangingMode = patch.mode;
        if (isChangingMode) {
            nextPatch = {
                mode: patch.mode
            };
        }
    }

    if (mode === deviceModes.BOOST && (nextPatch.heat || nextPatch.cool)) {
        const controlMode = yield select(getThermostatControlMode, {
            id: productId
        });

        yield putAppSettings({
            [buildKey(BOOST_TARGET, controlMode, productId)]: nextPatch.heat || nextPatch.cool
        });
    }

    const [, error] = yield call(handleApi(patchProduct), nextPatch, productType, productId);

    if (error) {
        yield put(changedProductState({ productId }));
        yield put(undoProductChange(type));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        return;
    }
    yield put(changedProductState({ productId }));
}

function* watchChangeProductStateSaga(): Generator<Effect, *, *> {
    yield takeLatest(CHANGE_PRODUCT_STATE, handleSaga(changeProductStateSaga));
}

export function* setHumidifierTypeSaga({ payload }: SetHumidifierTypeAction): Generator<Effect, *, *> {
    const nastats = yield select(getOnlineNaThermostats);
    yield nastats
        .map(p => call(handleApi(patchProduct), { humidifierType: payload }, 'nathermostat', p.get('id')))
        .toJS();
}

function* watchSetHumidifierTypeSaga(): Generator<Effect, *, *> {
    yield takeLatest(SET_HUMIDIFIER_TYPE, handleSaga(setHumidifierTypeSaga));
}

export default [watchCacheThermostatValue, watchChangeProductStateSaga, watchSetHumidifierTypeSaga];



// WEBPACK FOOTER //
// ./src/core/state/products/thermostat/sagas.js